import { Component, type ErrorInfo, type ReactNode } from "react";
import { C } from "@/lib/colors";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * App-level error boundary. Before this existed (added 2026-05-30) ANY
 * uncaught render error — most commonly a stale lazy-chunk rejection
 * after a deploy — unmounted the whole React tree and left a black
 * screen until a manual reload. This catches it and shows a recovery
 * card instead.
 *
 * Stale-chunk errors auto-reload once (the chunkReload guard usually
 * catches these earlier at the window level, but a React.lazy rejection
 * that surfaces as a render error lands here as a backstop). Any other
 * error shows a "Reload" button rather than reloading automatically, so
 * a genuine code bug doesn't spin the page in a refresh loop.
 */
function isStaleChunkError(err: Error | null): boolean {
  const m = err?.message || "";
  return (
    /dynamically imported module/i.test(m) ||
    /Importing a module script failed/i.test(m) ||
    /ChunkLoadError/i.test(err?.name || "")
  );
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for debugging; no remote logging wired yet.
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info.componentStack);
    if (isStaleChunkError(error)) {
      // One-shot reload, debounced via the same sessionStorage flag the
      // window-level guard uses so the two paths can't double-reload.
      try {
        const KEY = "finder:chunk-reloaded-at";
        const last = Number(sessionStorage.getItem(KEY) || "0");
        if (Date.now() - last > 10_000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
        }
      } catch {
        window.location.reload();
      }
    }
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const stale = isStaleChunkError(error);
    return (
      <div
        className="flex h-full w-full items-center justify-center"
        style={{ background: C.bg, padding: 24 }}
      >
        <div
          style={{
            maxWidth: 420,
            padding: 24,
            background: C.surface,
            border: `1px solid ${C.borderHi}`,
            borderRadius: 8,
          }}
        >
          <div
            className="font-mono-num uppercase"
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: stale ? C.amber : C.red,
              marginBottom: 8,
            }}
          >
            {stale ? "⟳ New version available" : "⚠ Something broke"}
          </div>
          <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.5, marginBottom: 16 }}>
            {stale
              ? "The dashboard was updated. Reloading to pick up the new version…"
              : "A page failed to render. Reload to recover — if it keeps happening, report it."}
          </div>
          <button
            type="button"
            onClick={() => {
              this.setState({ error: null });
              window.location.reload();
            }}
            className="font-mono-num uppercase cursor-pointer transition-colors duration-100"
            style={{
              padding: "6px 14px",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: C.text1,
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${C.borderHi}`,
              borderRadius: 4,
            }}
          >
            Reload
          </button>
          {!stale && (
            <pre
              className="font-mono-num"
              style={{
                marginTop: 14,
                fontSize: 10,
                color: C.text4,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 120,
                overflow: "auto",
              }}
            >
              {error.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
